import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, EffectCreative } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-creative";
import "swiper/css/pagination";

const WebinarRegistrationServices = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const services = [
    {
      title: "Email Marketing Campaigns",
      icon: "✉️",
      features: [
        "Precision audience targeting",
        "Multi-stage nurture sequences",
        "Interactive email elements",
        "Automated reminder emails",
        "Performance analytics dashboard",
      ],
      color: "from-purple-500 to-indigo-600",
    },
    {
      title: "Telecalling Outreach",
      icon: "📞",
      features: [
        "Bilingual calling teams",
        "Personalized invitation scripts",
        "Real-time registration assistance",
        "Objection handling training",
        "Call recording & QA",
      ],
      color: "from-blue-500 to-cyan-600",
    },
    {
      title: "Integrated Strategy",
      icon: "🔄",
      features: [
        "Omnichannel coordination",
        "Behavior-based follow-ups",
        "Lead scoring prioritization",
        "Cross-channel analytics",
        "Dynamic contact management",
      ],
      color: "from-orange-500 to-pink-600",
    },
  ];

  const successMetrics = [
    { value: "150%", label: "Registration Increase" },
    // { value: "20%", label: "Email Conversion Rate" },
    { value: "2x", label: "Attendance Boost" },
    { value: "10%+", label: "Guaranteed Attendance" },
  ];

  const processSteps = [
    {
      step: "1",
      title: "Audience Analysis",
      description:
        "We analyze your ideal attendee profile and market landscape",
    },
    {
      step: "2",
      title: "Content Strategy",
      description: "Crafting compelling messaging that drives registrations",
    },
    {
      step: "3",
      title: "Multi-Channel Launch",
      description: "Coordinated email and calling campaign execution",
    },
    {
      step: "4",
      title: "Performance Optimization",
      description: "Real-time adjustments based on engagement data",
    },
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 text-white py-28 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-20"></div>
        <div className="relative max-w-7xl mx-auto text-center">
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-serif"
          >
            Webinar Registration Services
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto"
          >
            Maximize attendance with our targeted multi-channel outreach
          </motion.p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-12 items-center"
        >
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 font-serif">
              <span className="relative pb-2 border-b-2 border-orange-500 inline-block">
                Amplify Your Webinar Impact
              </span>
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Our data-driven approach combines email marketing expertise with
              strategic telecalling to fill your virtual seats with qualified
              attendees. We don't just boost numbers - we deliver engaged
              participants genuinely interested in your content. Historically we
              have delivered 13% attendance.
            </p>
            <div className="space-y-4">
              {[
                "500K+ targeted B2B contacts",
                "Guaranteed minimum 10% attendance",
                "Multi-touch nurturing campaigns",
                "Real-time performance tracking",
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start"
                >
                  <svg
                    className="h-6 w-6 text-blue-500 mr-3 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-gray-700 font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeInUp} className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-20 blur-lg"></div>
            <div className="relative bg-white p-1 rounded-xl shadow-xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
                alt="Webinar presentation"
                className="w-full h-auto rounded-lg transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Our Comprehensive Approach
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Multi-channel strategies that drive registrations and engagement
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10 }}
                className={`bg-gradient-to-br ${service.color} text-white p-8 rounded-xl shadow-lg overflow-hidden relative group h-full`}
              >
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-all duration-300"></div>
                <div className="relative z-10 h-full flex flex-col">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg
                          className="h-5 w-5 text-white mr-2 mt-0.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Metrics */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Proven Results
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              We deliver measurable impact for every webinar campaign
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {successMetrics.map((metric, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
                className="bg-white p-8 rounded-xl shadow-md text-center"
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  whileInView={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                  className="text-4xl md:text-5xl font-bold text-blue-600 mb-3"
                >
                  {metric.value}
                </motion.div>
                <p className="text-gray-700 font-medium">{metric.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif"
            >
              Our Proven Process
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              A systematic approach to maximizing your webinar attendance
            </motion.p>
          </motion.div>

          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 h-full w-0.5 bg-gradient-to-b from-blue-500 to-orange-500"></div>

            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative mb-12 ${
                  index % 2 === 0 ? "lg:pr-24 text-left" : "lg:pl-24 text-right"
                }`}
              >
                <div
                  className={`bg-white p-6 rounded-xl shadow-md relative ${
                    index % 2 === 0 ? "lg:mr-8" : "lg:ml-8"
                  }`}
                >
                  <div
                    className={`absolute -top-4 ${
                      index % 2 === 0 ? "-left-4" : "-right-4"
                    } bg-blue-600 text-white text-xl w-12 h-12 rounded-full flex items-center justify-center font-bold`}
                  >
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Fill Your Webinar?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl mb-8 max-w-3xl mx-auto"
          >
            Let's discuss how we can maximize attendance for your next event.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            {/* <a
              href="tel:+441416282052"
              className="px-8 py-3 bg-white hover:bg-gray-100 text-blue-600 font-medium rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              +44 1416 282 052
            </a> */}
            <a
              href="/contact"
              className="px-8 py-3 border-2 border-white text-white font-medium rounded-lg transition-all duration-300 hover:bg-white hover:text-blue-600"
            >
              Get a Quote
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default WebinarRegistrationServices;
